import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Cloud, Loader2, Plus, Upload, X } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon";
import type { SelectedDriveFile } from "@/components/wizard/GoogleDrivePicker";
import {
  downloadGoogleDriveFile,
  getGoogleDriveStatus,
  googleDriveDownloadToFile,
  isGoogleDriveReconnectRequiredError,
  startGoogleDriveConnect,
} from "@/lib/api/google-drive";
import {
  KM_PICKER_MIME_TYPES,
  canUseIframePicker,
  getGoogleDriveReturnPath,
  GOOGLE_DRIVE_IFRAME_COOKIE_HELP,
  GOOGLE_DRIVE_INLINE_PICKER_HELP,
  GOOGLE_DRIVE_NEW_TAB_HELP,
  openGoogleDriveOnePick,
  openGooglePicker,
} from "@/lib/google-picker";
import { useDriveOnePickReturn } from "@/hooks/useDriveOnePickReturn";
import { useGoogleDriveConnectReturn } from "@/hooks/useGoogleDriveConnectReturn";
import {
  getSourceFileType,
  MAX_MANUAL_SOURCE_FILES,
  SourcesDropzone,
  type SourcesDropzoneHandle,
} from "./SourcesDropzone.js";

type SourceAction = "upload" | "drive" | "onedrive" | "dropbox" | "sharepoint";

interface StagedFile {
  id: string;
  file: File;
}

export interface SourceUploadItem {
  file: File;
  title?: string;
}

interface SourcesPanelProps {
  compact?: boolean;
  showHeader?: boolean;
  className?: string;
  uploading?: boolean;
  onUpload?: (items: SourceUploadItem[]) => Promise<void>;
  /** Upload imported Drive files immediately instead of staging them for manual confirm. */
  autoUploadAfterDriveImport?: boolean;
  onExternalPickerActiveChange?: (active: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
}

const DISABLED_ACTIONS: SourceAction[] = ["onedrive", "dropbox", "sharepoint"];

const FILE_LIMIT_ERROR = "You can upload up to 10 files at a time.";

const TYPE_BADGE_COLORS: Record<string, string> = {
  pdf: "bg-red-500/20 text-red-300",
  docx: "bg-blue-500/20 text-blue-300",
  pptx: "bg-orange-500/20 text-orange-300",
  xlsx: "bg-emerald-500/20 text-emerald-300",
  csv: "bg-emerald-500/20 text-emerald-300",
  html: "bg-violet-500/20 text-violet-300",
  md: "bg-slate-500/20 text-slate-300",
};

function fileKey(file: File): string {
  return `${file.name}-${file.size}`;
}

function appendUniqueFiles(prev: StagedFile[], files: File[]): StagedFile[] {
  const existing = new Set(prev.map((item) => item.id));
  const next = [...prev];

  for (const file of files) {
    const id = fileKey(file);
    if (!existing.has(id)) {
      existing.add(id);
      next.push({ id, file });
    }
  }

  return next;
}

export function SourcesPanel({
  compact = false,
  showHeader = true,
  className,
  uploading = false,
  onUpload,
  autoUploadAfterDriveImport = false,
  onExternalPickerActiveChange,
  onBusyChange,
}: SourcesPanelProps) {
  const dropzoneRef = useRef<SourcesDropzoneHandle>(null);
  const [activeAction, setActiveAction] = useState<SourceAction>("upload");
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [driveAvailable, setDriveAvailable] = useState(false);
  const [driveReconnectRequired, setDriveReconnectRequired] = useState(false);
  const [drivePickerOpening, setDrivePickerOpening] = useState(false);
  const [driveImporting, setDriveImporting] = useState(false);
  const [internalUploading, setInternalUploading] = useState(false);

  const isUploading = uploading || internalUploading || driveImporting || drivePickerOpening;

  useEffect(() => {
    onBusyChange?.(driveImporting || drivePickerOpening || internalUploading);
  }, [driveImporting, drivePickerOpening, internalUploading, onBusyChange]);

  const refreshDriveStatus = useCallback(async () => {
    try {
      const status = await getGoogleDriveStatus();
      setDriveAvailable(status.connected && !status.reconnectRequired);
      setDriveReconnectRequired(status.reconnectRequired);
    } catch {
      setDriveAvailable(false);
    }
  }, []);

  useEffect(() => {
    void refreshDriveStatus();
  }, [refreshDriveStatus]);

  const actions: {
    id: SourceAction;
    label: string;
    icon: React.ReactNode;
    disabled?: boolean;
  }[] = [
    { id: "upload", label: "Upload files", icon: <Plus className="h-[18px] w-[18px]" /> },
    { id: "drive", label: "Add from Google Drive", icon: <GoogleDriveIcon /> },
    {
      id: "onedrive",
      label: "OneDrive",
      icon: <Cloud className="h-[18px] w-[18px]" />,
      disabled: true,
    },
    {
      id: "dropbox",
      label: "Dropbox",
      icon: <Cloud className="h-[18px] w-[18px]" />,
      disabled: true,
    },
    ...(compact
      ? []
      : [
          {
            id: "sharepoint" as const,
            label: "SharePoint",
            icon: <Cloud className="h-[18px] w-[18px]" />,
            disabled: true,
          },
        ]),
  ];

  const addManualFiles = useCallback(
    (files: File[]) => {
      setActiveAction("upload");
      const remaining = MAX_MANUAL_SOURCE_FILES - stagedFiles.length;
      if (remaining <= 0) {
        setError(FILE_LIMIT_ERROR);
        return;
      }

      const toAdd = files.slice(0, remaining);
      if (toAdd.length < files.length) {
        setError(FILE_LIMIT_ERROR);
      } else {
        setError(null);
      }

      setStagedFiles((prev) => appendUniqueFiles(prev, toAdd));
    },
    [stagedFiles.length]
  );

  const addDriveFiles = useCallback((files: File[]) => {
    setError(null);
    setActiveAction("upload");
    setStagedFiles((prev) => appendUniqueFiles(prev, files));
  }, []);

  const removeFile = useCallback((id: string) => {
    setStagedFiles((prev) => prev.filter((item) => item.id !== id));
    setError(null);
  }, []);

  async function handleConfirmUpload() {
    if (stagedFiles.length === 0 || !onUpload || isUploading) return;

    try {
      await uploadImportedFiles(stagedFiles.map((item) => item.file));
    } catch {
      // Error state is set in uploadImportedFiles.
    }
  }

  const importDriveFiles = useCallback(async (selected: SelectedDriveFile[]): Promise<File[]> => {
    if (selected.length === 0) return [];

    setDriveImporting(true);
    setError(null);
    setActiveAction("upload");

    try {
      const downloadedFiles: File[] = [];
      for (const file of selected) {
        const downloaded = await downloadGoogleDriveFile({
          fileId: file.id,
          resourceKey: file.resourceKey,
          accessToken: file.accessToken,
        });
        downloadedFiles.push(googleDriveDownloadToFile(downloaded));
      }
      return downloadedFiles;
    } catch (err) {
      if (isGoogleDriveReconnectRequiredError(err)) {
        setDriveAvailable(false);
        setDriveReconnectRequired(true);
        setError("Google Drive authorization has expired. Reconnect to continue.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to import from Google Drive");
      }
      setActiveAction("drive");
      throw err;
    } finally {
      setDriveImporting(false);
    }
  }, []);

  const uploadImportedFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || !onUpload) return;

      setInternalUploading(true);
      setError(null);

      try {
        await onUpload(files.map((file) => ({ file })));
        setStagedFiles([]);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed");
        throw err;
      } finally {
        setInternalUploading(false);
      }
    },
    [onUpload]
  );

  const handleDriveFilesSelect = useCallback(
    async (selected: SelectedDriveFile[]) => {
      const downloadedFiles = await importDriveFiles(selected);
      if (downloadedFiles.length === 0) return;

      if (autoUploadAfterDriveImport) {
        await uploadImportedFiles(downloadedFiles);
        return;
      }

      addDriveFiles(downloadedFiles);
    },
    [addDriveFiles, autoUploadAfterDriveImport, importDriveFiles, uploadImportedFiles]
  );

  const handleOnePickImport = useCallback(
    async ({ files }: { fileIds: string[]; files: File[] }) => {
      if (files.length === 0) return;

      setActiveAction("drive");
      setError(null);

      if (autoUploadAfterDriveImport) {
        await uploadImportedFiles(files);
        return;
      }

      addDriveFiles(files);
    },
    [addDriveFiles, autoUploadAfterDriveImport, uploadImportedFiles]
  );

  useDriveOnePickReturn({
    onImport: handleOnePickImport,
    onError: (message) => {
      if (message.includes("expired")) {
        setDriveAvailable(false);
        setDriveReconnectRequired(true);
      }
      setError(message);
      setActiveAction("drive");
    },
  });

  useGoogleDriveConnectReturn({
    onConnected: () => {
      setActiveAction("drive");
      void refreshDriveStatus();
    },
    onError: (message) => {
      setError(message);
      setActiveAction("drive");
    },
  });

  async function handleConnect() {
    setError(null);
    try {
      const { url } = await startGoogleDriveConnect(getGoogleDriveReturnPath());
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start Google Drive connection");
      setActiveAction("drive");
    }
  }

  const launchDriveOnePick = useCallback(async () => {
    if (!driveAvailable || drivePickerOpening || uploading || internalUploading || driveImporting) {
      return;
    }

    setDrivePickerOpening(true);
    setError(null);
    setActiveAction("drive");

    try {
      await openGoogleDriveOnePick({
        multiple: true,
        mimeTypes: KM_PICKER_MIME_TYPES,
        returnPath: getGoogleDriveReturnPath(),
      });
    } catch (err) {
      if (isGoogleDriveReconnectRequiredError(err)) {
        setDriveAvailable(false);
        setDriveReconnectRequired(true);
        setError("Google Drive authorization has expired. Reconnect to continue.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to open Google Drive in a new tab");
      }
    } finally {
      setDrivePickerOpening(false);
    }
  }, [
    driveAvailable,
    driveImporting,
    drivePickerOpening,
    internalUploading,
    uploading,
  ]);

  const launchDriveIframePicker = useCallback(async () => {
    if (!driveAvailable || drivePickerOpening || uploading || internalUploading || driveImporting) {
      return;
    }

    setDrivePickerOpening(true);
    setError(null);
    setActiveAction("drive");

    try {
      const useIframe = await canUseIframePicker();
      if (!useIframe) {
        setError(
          `Embedded picker unavailable. ${GOOGLE_DRIVE_IFRAME_COOKIE_HELP} Use Browse Google Drive to open in a new tab instead.`
        );
        setActiveAction("drive");
        return;
      }

      flushSync(() => {
        onExternalPickerActiveChange?.(true);
      });

      const selected = await openGooglePicker({
        multiple: true,
        mimeTypes: KM_PICKER_MIME_TYPES,
      });

      if (selected.length === 0) {
        setActiveAction("drive");
        return;
      }

      const mapped: SelectedDriveFile[] = selected.map((file) => ({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        size: file.size,
        resourceKey: file.resourceKey ?? null,
        accessToken: file.accessToken,
      }));

      await handleDriveFilesSelect(mapped);
    } catch (err) {
      if (isGoogleDriveReconnectRequiredError(err)) {
        setDriveAvailable(false);
        setDriveReconnectRequired(true);
        setError("Google Drive authorization has expired. Reconnect to continue.");
      } else {
        setError(
          (current) =>
            current ??
            (err instanceof Error ? err.message : "Failed to open Google Drive picker")
        );
      }
      setActiveAction("drive");
    } finally {
      setDrivePickerOpening(false);
      setActiveAction("drive");
      window.requestAnimationFrame(() => {
        onExternalPickerActiveChange?.(false);
      });
    }
  }, [
    driveAvailable,
    drivePickerOpening,
    driveImporting,
    handleDriveFilesSelect,
    internalUploading,
    onExternalPickerActiveChange,
    uploading,
  ]);

  function handleSidebarAction(actionId: SourceAction) {
    setActiveAction(actionId);
    setError(null);

    if (actionId === "upload") {
      dropzoneRef.current?.openFilePicker();
    }
  }

  const uploadLabel =
    stagedFiles.length === 0
      ? "Upload"
      : stagedFiles.length === 1
        ? "Upload file"
        : `Upload ${stagedFiles.length} files`;

  return (
    <>
      <div
        className={cn(
          "overflow-hidden rounded-2xl border border-glass-border bg-glass-bg backdrop-blur-md",
          compact && "flex min-h-0 flex-1 flex-col",
          className
        )}
      >
        {showHeader ? (
          <div className="border-b border-[var(--lm-border-subtle)] px-5 py-4 dark:border-white/10">
            <h3 className="text-base font-semibold text-[var(--lm-foreground-strong)] dark:text-white">Sources</h3>
            <p className="mt-1 text-xs text-[var(--lm-muted)] dark:text-slate-400">
              Add documents this library can use for KM answers
            </p>
          </div>
        ) : null}

        <div
          className={cn(
            "flex flex-col md:flex-row",
            compact ? "min-h-0 flex-1" : "min-h-[240px]"
          )}
        >
          <div
            className={cn(
              "flex shrink-0 flex-col gap-1.5 border-b border-[var(--lm-border-subtle)] p-3 dark:border-white/10 md:border-b-0 md:border-r",
              compact ? "w-full md:w-[240px]" : "w-full md:w-[220px]"
            )}
          >
            {actions.map((action) => {
              const isDisabled = action.disabled || DISABLED_ACTIONS.includes(action.id);
              return (
                <Button
                  key={action.id}
                  type="button"
                  variant="ghost"
                  disabled={isDisabled || isUploading}
                  onClick={() => {
                    if (isDisabled || isUploading) return;
                    handleSidebarAction(action.id);
                  }}
                  className={cn(
                    "h-auto w-full justify-start gap-2.5 rounded-xl border px-3 py-2.5 text-left text-[13px] font-normal text-[var(--lm-foreground-strong)] dark:text-white",
                    activeAction === action.id
                      ? "border-violet-500/40 bg-violet-500/12 hover:bg-violet-500/12"
                      : "border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] hover:border-violet-500/35 hover:bg-[var(--lm-hover)] dark:border-white/10 dark:bg-slate-900/45 dark:hover:bg-white/5",
                    (isDisabled || isUploading) && "cursor-not-allowed opacity-45"
                  )}
                >
                  {action.icon}
                  <span className="min-w-0 flex-1">{action.label}</span>
                  {isDisabled ? (
                    <span className="shrink-0 rounded-full bg-slate-500/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-slate-400">
                      Soon
                    </span>
                  ) : null}
                </Button>
              );
            })}
          </div>

          <div
            className={cn(
              "flex flex-1 flex-col bg-[var(--surface-sunken)] p-4 dark:bg-slate-950/25",
              compact && "min-h-0"
            )}
          >
            {activeAction === "upload" ? (
              <div className={cn("flex flex-1 flex-col gap-4", compact && "min-h-0")}>
                <SourcesDropzone
                  ref={dropzoneRef}
                  onFilesSelected={addManualFiles}
                  onInvalidFiles={() =>
                    setError(
                      "Some files were skipped. Supported: PDF, DOCX, PPTX, HTML, MD, CSV, XLSX."
                    )
                  }
                  onFileLimitExceeded={() => setError(FILE_LIMIT_ERROR)}
                  disabled={isUploading}
                  className="min-h-[140px] shrink-0"
                />

                {driveImporting ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin text-violet-300" />
                    Importing from Google Drive…
                  </div>
                ) : null}

                {stagedFiles.length > 0 ? (
                  <div
                    className={cn(
                      "flex flex-wrap gap-3",
                      compact && "max-h-[220px] overflow-y-auto"
                    )}
                  >
                    {stagedFiles.map((item) => {
                      const type = getSourceFileType(item.file.name);
                      return (
                        <div
                          key={item.id}
                          className="group relative flex h-[100px] w-[100px] flex-col rounded-xl border border-[var(--lm-border-subtle)] bg-white p-2.5 shadow-[var(--shadow-card)] hover:border-violet-500/35 dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none"
                        >
                          <span
                            className={cn(
                              "w-fit rounded px-1.5 py-0.5 text-[9px] font-bold uppercase",
                              TYPE_BADGE_COLORS[type] ?? "bg-slate-500/20 text-slate-300"
                            )}
                          >
                            {type}
                          </span>
                          <span className="mt-auto line-clamp-2 text-[11px] leading-tight text-[var(--lm-foreground-strong)] dark:text-white">
                            {item.file.name}
                          </span>
                          <IconButton
                            type="button"
                            size="xs"
                            variant="destructive"
                            aria-label="Remove"
                            disabled={isUploading}
                            onClick={() => removeFile(item.id)}
                            className="absolute right-1 top-1 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed"
                          >
                            <X className="h-3 w-3" />
                          </IconButton>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {error ? <p className="text-sm text-red-400">{error}</p> : null}

                <div className="mt-auto flex shrink-0 justify-end">
                  <Button
                    variant="outline"
                    disabled={stagedFiles.length === 0 || isUploading || !onUpload}
                    onClick={() => void handleConfirmUpload()}
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {uploadLabel}
                  </Button>
                </div>
              </div>
            ) : activeAction === "drive" ? (
              <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-800/80 text-slate-300">
                  <GoogleDriveIcon className="h-8 w-8" />
                </div>
                {driveReconnectRequired ? (
                  <>
                    <p className="text-[13px] font-medium text-slate-200">
                      Google Drive authorization expired
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Reconnect your Google account to import documents from Drive
                    </p>
                    <Button variant="accent" className="mt-4" onClick={() => void handleConnect()}>
                      Reconnect Google Drive
                    </Button>
                  </>
                ) : driveAvailable ? (
                  <>
                    <p className="text-[13px] font-medium text-slate-200">
                      Select from Google Drive
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      PDF, Word, PowerPoint, Excel, HTML, Markdown, and CSV from your Drive
                    </p>
                    <Button
                      variant="accent"
                      className="mt-4 gap-2"
                      disabled={isUploading}
                      onClick={() => void launchDriveOnePick()}
                    >
                      {drivePickerOpening ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Opening Drive…
                        </>
                      ) : (
                        "Browse Google Drive"
                      )}
                    </Button>
                    <p className="mt-3 max-w-sm text-[10px] leading-relaxed text-slate-500">
                      {GOOGLE_DRIVE_NEW_TAB_HELP}
                    </p>
                    <button
                      type="button"
                      className="mt-3 text-[11px] text-violet-300 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isUploading}
                      onClick={() => void launchDriveIframePicker()}
                    >
                      Try embedded picker inside the app
                    </button>
                    <p className="mt-2 max-w-sm text-[10px] leading-relaxed text-slate-500">
                      {GOOGLE_DRIVE_INLINE_PICKER_HELP}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[13px] font-medium text-slate-200">
                      Google Drive not connected
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Connect your Google account to import documents
                    </p>
                    <Button variant="accent" className="mt-4" onClick={() => void handleConnect()}>
                      Connect Google Drive
                    </Button>
                  </>
                )}
                {error ? <p className="mt-4 text-sm text-red-400">{error}</p> : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

    </>
  );
}
